import { paths } from '@mc/paths'
import { trackGoldStandardPurchaseClick } from '@mc/pre-paywall-events'
import {
  Locations,
  NavigationEvents,
  ProductLine as TrackProduct,
  trackGoldStandardEvent,
  track,
  getPageSource,
} from '@mc/track-event'

type NavLevel =
  (typeof NavigationEvents.LEVELS)[keyof typeof NavigationEvents.LEVELS]

type NavLocation = (typeof Locations)[keyof typeof Locations]

export const productTypeTrackingMap = {
  consumer: TrackProduct.MASTERCLASS,
  certificates: TrackProduct.CERTIFICATES,
  enterprise: TrackProduct.AT_WORK,
  executive: TrackProduct.EXECUTIVE,
} as const

export const trackNavClick =
  (
    action: string,
    level: NavLevel = NavigationEvents.LEVELS.PRIMARY,
    location: NavLocation = Locations.NAVIGATION,
    product?: string,
  ) =>
  () => {
    const pageSource = getPageSource()

    const eventProperties: {
      action: string
      level: NavLevel
      location: NavLocation
      page_source: string | undefined
      product?: string
    } = {
      action,
      level,
      location,
      page_source: pageSource,
    }

    if (product) {
      eventProperties.product = product
    }

    trackGoldStandardEvent(
      NavigationEvents.EVENTS.NAVIGATION_CLICKED,
      eventProperties,
    )
  }

export const trackGiftCTAClick = ({
  product,
  level = NavigationEvents.LEVELS.SECONDARY,
}: {
  product?: string
  level?: NavLevel
}) => {
  // The gift nav link is not a purchase-funnel CTA: fire "Navigation Clicked"
  // only (no "Purchase Click").
  trackNavClick('Gift MasterClass', level, undefined, product)()
}

const trackViewPlansNavClick = (level: NavLevel, product?: string) => {
  trackGoldStandardEvent(NavigationEvents.EVENTS.NAVIGATION_CLICKED, {
    category: 'home-nav-header',
    action: 'View Plans',
    level,
    page_source: getPageSource(),
    product,
  })
}

const trackViewPlansPurchaseClick = (level: NavLevel, product?: string) => {
  // Fires the gold-standard "Purchase Click".
  trackGoldStandardPurchaseClick({
    action: 'View Plans',
    location: 'navigation',
    href: paths.checkout,
    purchaseClickProps: { level, product },
  })
}

export const trackPlansClick = (level: NavLevel, product?: string) => () => {
  trackViewPlansNavClick(level, product)
  trackViewPlansPurchaseClick(level, product)
}

export const trackNavigationClick = (
  action: string,
  level: NavLevel,
  location: NavLocation,
  product?: string,
) => {
  const properties: Record<
    string,
    string | number | boolean | object | null | undefined
  > = {
    action,
    level,
    location,
    page_source: getPageSource(),
  }

  if (product) {
    properties.product = product
  }
  const options: { integrations?: Record<string, boolean> } = {
    integrations: {
      All: true,
      'Bing Ads': false,
      'Facebook CAPI': false,
      'Facebook Pixel': false,
      'Google AdWords New': false,
      'Google Tag Manager': false,
      Hotjar: false,
      ShareASale: false,
      TikTok: false,
    },
  }

  track(NavigationEvents.EVENTS.NAVIGATION_CLICKED, properties, options)
}
